/**
 * API Keys
 *
 * Customer-facing these are "API Keys". The backend entity is an API Project
 * (`api_projects`), and each one holds one credential pair:
 *
 *   - clientId     public, always visible, prefixed ck_live_ / ck_test_
 *   - clientSecret returned ONCE on create and once on each rotation
 *
 * The partner exchanges the pair for a 1-hour JWT at
 * POST /api/client/v1/oauth/token. There is no static bearer key.
 */

export interface ApiKey {
    id: number;
    uuid: string;
    organizationId: number;
    name: string;
    active: boolean;
    scopes: Array<string>;
    /**
     * Locations this key can reach.
     *
     * The backend treats an EMPTY marketplace_ids array as "the whole
     * organization subtree", not "no access". The converter turns that into the
     * explicit `allLocations` flag below so the ambiguity never reaches the UI.
     */
    locationIds: Array<number>;
    allLocations: boolean;
    clientId: string | null;
    expiresAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface SaveApiKey {
    name: string;
    scopes: Array<string>;
    /** Ignored when `allLocations` is true. */
    locationIds: Array<number>;
    allLocations: boolean;
    expiresAt?: string | null;
}

/**
 * Returned by create and rotate only.
 *
 * `secret` is deliberately NOT part of `ApiKey` and never reaches the Redux
 * store: it is a production credential and the store is visible in DevTools.
 * The thunks return it to the caller, which holds it in local component state
 * for the lifetime of the modal and then drops it.
 */
export interface IssuedApiKeySecret {
    apiKey: ApiKey;
    secret: string;
}

/* -------------------------------------------------------------------------- */
/* Scope catalog                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Mirror of `API::PartnerPermissionCatalog` in the backend
 * (app/models/api/partner_permission_catalog.rb).
 *
 * The backend does not expose the catalog over HTTP yet, so this list is
 * maintained by hand and will drift if a resource is added there. A follow-up
 * ticket asks backend for GET /api/v1/api_scopes so this can be fetched.
 *
 * Note the naming: the scope is `clients`, not `customers`.
 */
export const API_SCOPE_ACTIONS = ['read', 'write', 'manage'] as const;

export type ApiScopeAction = (typeof API_SCOPE_ACTIONS)[number];

export const API_SCOPE_ACTION_LABELS: Record<ApiScopeAction, string> = {
    read: 'Read',
    write: 'Write',
    manage: 'Manage'
};

export interface ApiScopeResource {
    /** Value used to build `<resource>:<action>`. */
    resource: string;
    label: string;
    /**
     * The actions the backend actually accepts for this resource. Most take
     * read and write, but `reports` is read-only and `exports` only takes
     * `manage`, so the catalog declares them instead of the picker assuming.
     */
    actions: Array<ApiScopeAction>;
    /**
     * True when no endpoint answers for this resource yet. The backend accepts
     * the scope and nothing responds to it, so the picker leaves these out
     * entirely — an administrator cannot act on them, and listing them would
     * put roadmap language in a customer-facing screen.
     *
     * Clear the flag here when the endpoints land and the picker follows.
     */
    unavailable?: boolean;
}

const READ_WRITE: Array<ApiScopeAction> = ['read', 'write'];

export const API_SCOPE_RESOURCES: Array<ApiScopeResource> = [
    { resource: 'clients', label: 'Clients', actions: READ_WRITE },
    { resource: 'pets', label: 'Pets', actions: READ_WRITE },
    { resource: 'vaccinations', label: 'Vaccinations', actions: READ_WRITE },
    { resource: 'notes', label: 'Notes', actions: READ_WRITE },
    { resource: 'appointments', label: 'Appointments', actions: READ_WRITE },
    { resource: 'bookings', label: 'Bookings', actions: READ_WRITE },
    { resource: 'services', label: 'Services', actions: READ_WRITE },
    { resource: 'addons', label: 'Add-ons', actions: READ_WRITE },
    { resource: 'products', label: 'Products', actions: READ_WRITE },
    { resource: 'invoices', label: 'Invoices', actions: READ_WRITE },
    { resource: 'orders', label: 'Orders', actions: READ_WRITE },
    { resource: 'staff', label: 'Staff', actions: READ_WRITE },
    { resource: 'locations', label: 'Locations', actions: READ_WRITE },

    // The backend catalog lists these two outside the resource/action grid:
    // only `reports:read` and `exports:manage` exist. They are ordinary rows
    // here, with the single action each one really has.
    { resource: 'reports', label: 'Reports', actions: ['read'] },
    { resource: 'exports', label: 'Bulk exports', actions: ['manage'] },

    // Accepted by the backend, answered by nothing. Hidden from the picker
    // until the endpoints exist.
    { resource: 'tasks', label: 'Tasks', actions: READ_WRITE, unavailable: true },
    { resource: 'rooms', label: 'Rooms', actions: READ_WRITE, unavailable: true },
    { resource: 'availability', label: 'Availability', actions: READ_WRITE, unavailable: true },
    { resource: 'payments', label: 'Payments', actions: READ_WRITE, unavailable: true },
    { resource: 'refunds', label: 'Refunds', actions: READ_WRITE, unavailable: true },
    { resource: 'memberships', label: 'Memberships', actions: READ_WRITE, unavailable: true },
    { resource: 'enrollments', label: 'Enrollments', actions: READ_WRITE, unavailable: true },
    { resource: 'agreements', label: 'Agreements', actions: READ_WRITE, unavailable: true }
];

export const buildScope = (resource: string, action: ApiScopeAction): string => `${resource}:${action}`;

/** Every scope the backend accepts. Used to validate before sending. */
export const ALL_API_SCOPES: Array<string> = API_SCOPE_RESOURCES.flatMap(({ resource, actions }) =>
    actions.map(action => buildScope(resource, action))
);

export const isKnownScope = (scope: string): boolean => ALL_API_SCOPES.includes(scope);
